import { CompactLabel } from 'app/components/CompactLabel'
import { cn } from 'app/lib/utils'

export type SegmentedOption<T> = {
  id: T
  label: string
  /** What the option says when its toolbar narrows. See CompactLabel. */
  shortLabel?: string
  icon?: React.ReactNode
  /** Native tooltip: what picking this one actually shows you. */
  title?: string
}

/**
 * One pill of mutually exclusive choices, shared by the band pickers, the
 * sort picker and the view toggle.
 *
 * `h-8` on purpose: the height of the shared `<Search />` box these sit
 * beside, so the control row lines up.
 */
export function SegmentedControl<T>({
  options,
  value,
  onSelect,
  className,
}: {
  options: SegmentedOption<T>[]
  value: T
  onSelect: (id: T) => void
  className?: string
}) {
  return (
    <div
      className={cn(
        `inline-flex h-8 shrink-0 items-center gap-0.5 rounded-lg border border-default bg-surface-subtle p-0.5`,
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.id === value
        return (
          <button
            // An option's id can legitimately be undefined ("All" is the
            // absence of a band), so the index backs the key up.
            key={String(option.id ?? index)}
            type="button"
            title={option.title}
            aria-pressed={selected}
            onClick={() => onSelect(option.id)}
            className={cn(
              // Tighter at the icon tier, where a label no longer needs the room: it is what
              // lets a phone's second control line hold the switch, band and sort together.
              `inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-body-xs transition @max-xl/toolbar:px-1.5`,
              // Weight as well as paper: on a control this quiet the white
              // pill alone was doing all the work of saying which one is on,
              // and it reads as a raised tile rather than as a choice made.
              selected
                ? `bg-surface font-medium text-strong shadow-elevation-1`
                : `text-subtle hover:bg-surface/70 hover:text-strong`,
            )}
          >
            {option.icon}
            <CompactLabel full={option.label} short={option.shortLabel} iconOnly={!!option.icon} />
          </button>
        )
      })}
    </div>
  )
}
